import styles from "./BrugerList.module.scss";
import NextPreviousPage from "@/components/common/NextPreviousPage/NextPreviousPage";
import { BrugerePageOptions } from "@/interfaces/paging";
import { motion } from "motion/react";
import Button from "@/components/common/Buttons/Button";
import { Filter } from "@/routes/Fejllister/MainContent/Header/AktiveFiltre";
import { Kommune, kommuner } from "@/util/index";
import IconFilter from "@/components/common/icons/IconFilter";
import KommuneMultipleSelectCombobox from "@/components/common/KommuneMultipleSelectCombobox/KommuneMultipleSelectCombobox";
import { Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { BrugerSortOrder } from "@/interfaces/Opgaveliste/enums";
import ChevronUp from "@/components/common/icons/ChevronUp";
import ChevronDown from "@/components/common/icons/ChevronDown";
import Select from "@/components/common/Select/Select";

interface BrugerListProps {
  pageOptions: BrugerePageOptions;
  setPageOptions: Dispatch<SetStateAction<BrugerePageOptions>>;
  totalCount: number;
  brugere: Bruger[];
  showFilter: boolean;
  setShowFilter: Dispatch<SetStateAction<boolean>>;
  brugernavn: string;
  setBrugernavn: Dispatch<SetStateAction<string>>;
  kommunenr: string;
  setKommunenr: (knr: string) => void;
  brugernavnFilter: string | undefined;
  kommunenrFilter: string | undefined;
  roller: Rolle[];
  rolleId: string;
  setRolleId: (rolleId: string) => void;
}
export default function BrugerList(props: BrugerListProps) {
  const {
    pageOptions,
    setPageOptions,
    totalCount,
    brugere,
    brugernavn,
    setBrugernavn,
    brugernavnFilter,
    kommunenrFilter,
    setShowFilter,
    showFilter,
    kommunenr,
    setKommunenr,
    roller,
    rolleId,
    setRolleId,
  } = props;
  const navigate = useNavigate();

  const skatPlusKommuner = [{ knr: 0, navn: "Skat" }, ...kommuner];
  const kommunenummerFilter: number[] = kommunenrFilter
    ? kommunenrFilter.split(",").map((s) => parseInt(s))
    : [];

  const setKommuneIsActive = (
    value: number,
    isActive: boolean,
    push: boolean = false
  ) => {
    let kommunenumre: number[] = kommunenr
      ? kommunenr.split(",").map((knr) => parseInt(knr))
      : [];

    if (isActive) {
      if (kommunenumre.indexOf(value) === -1) {
        kommunenumre.push(value);
        setKommunenr(kommunenumre.join(","));
        if (push)
          setPageOptions({ ...pageOptions, kommunenr: kommunenumre.join(",") });
      }
    } else {
      if (kommunenumre.indexOf(value) > -1) {
        kommunenumre = kommunenumre.filter((b) => b !== value);
        setKommunenr(kommunenumre.length ? kommunenumre.join(",") : "");
        if (push)
          setPageOptions({ ...pageOptions, kommunenr: kommunenumre.join(",") });
      }
    }
  };
  const kommuneComboboxSelectedItems =
    kommunenr.length > 0
      ? kommunenr.split(",").map((k) => {
          const knr = parseInt(k);
          return {
            knr,
            navn: skatPlusKommuner.find((k) => k.knr === knr)?.navn!,
          } as Kommune;
        })
      : [];
  const selectRoller: Rolle[] = [
    { id: "-1", navn: "" },
    { id: "00000000-0000-0000-0000-000000000000", navn: "Ingen" },
    ...roller,
  ];
  const rolleFilterValue = () => {
    const r = roller.find((r) => r.id.toString() === pageOptions.rolleid);
    if (!r) return "Ingen roller";
    return r.navn.charAt(0).toLocaleUpperCase() + r.navn.slice(1);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-5">
        <h1 className="mb-0">Brugere</h1>
        <div className="d-flex gap-3">
          <button
            className="c-filter-btn btn border btn--icon tooltip-center tooltip-top"
            onClick={() => setShowFilter(!showFilter)}
            aria-label="Filtrér"
          >
            <IconFilter width={22} />
          </button>
          <NextPreviousPage
            pageOptions={pageOptions}
            setPageOptions={setPageOptions}
            totalCount={totalCount}
          />
        </div>
      </div>
      {showFilter && (
        <div className={`border border-1 px-3 py-3 rounded mb-4`}>
          <h2>Filtre</h2>
          <div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="d-flex flex-column h-100 justify-content-between flex-grow-1">
                <label htmlFor="" className="fs-5">
                  Navn
                </label>
                <input
                  style={{ marginBottom: 0, padding: "0.65rem 0.3rem" }}
                  className="border rounded"
                  value={brugernavn}
                  onChange={(e) => setBrugernavn(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter")
                      setPageOptions({ ...pageOptions, navn: brugernavn });
                  }}
                />
              </div>
              <div className="mb-1 flex-grow-1">
                <label htmlFor="" className="fs-5">
                  Organisation
                </label>
                <KommuneMultipleSelectCombobox
                  includeSkat
                  selectedItems={kommuneComboboxSelectedItems}
                  setSelectedItems={(selectedItems) => {
                    setKommunenr(selectedItems.map((k) => k.knr).join(","));
                  }}
                  inputClassName="border rounded"
                  inputStyle={{
                    height: 51,
                    marginBottom: 0,
                  }}
                  menuStyle={{
                    top: 50,
                  }}
                />
              </div>
              <div className="mb-1 flex-grow-1">
                <label htmlFor="" className="fs-5">
                  Roller
                </label>
                <Select
                  toggleClassName="border rounded"
                  items={selectRoller}
                  itemKey={"id"}
                  itemToString={(item) =>
                    item
                      ? item.navn.charAt(0).toLocaleUpperCase() +
                        item.navn.slice(1)
                      : ""
                  }
                  placeholder="Vælg roller"
                  selectedItem={
                    selectRoller.find((r) => r.id.toString() === rolleId) ??
                    selectRoller[0]
                  }
                  setSelectedItem={(selectedItem) => {
                    if (selectedItem) {
                      if (selectedItem.id === "-1") {
                        setRolleId("");
                      } else {
                        setRolleId(selectedItem.id.toString());
                      }
                    }
                  }}
                  itemIsSelected={(item) => {
                    if (!rolleId) return item.id === "-1";
                    return item.id.toString() === rolleId;
                  }}
                  toggleStyle={{ minWidth: "200px", height: "51px" }}
                  menuStyle={{ top: 50 }}
                />
              </div>
            </div>
            <div className="d-flex justify-content-end align-items-center gap-3 mt-3">
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  alignSelf: "end",
                  marginLeft: "auto",
                }}
              >
                <Button
                  type="primary"
                  // disabled={brugernavn === brugernavnFilter}
                  onClick={() => {
                    setBrugernavn("");
                    setKommunenr("");
                    setRolleId("");
                    setPageOptions({
                      ...pageOptions,
                      kommunenr: "",
                      navn: "",
                      rolleid: "",
                    });
                  }}
                >
                  Ryd filtre
                </Button>
                <Button
                  type="small-filled"
                  disabled={
                    brugernavn === brugernavnFilter &&
                    kommunenr === kommunenrFilter &&
                    rolleId === pageOptions.rolleid
                  }
                  onClick={() => {
                    setPageOptions({
                      ...pageOptions,
                      navn: brugernavn,
                      kommunenr,
                      rolleid: rolleId,
                    });
                  }}
                >
                  Filtrer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        {brugernavnFilter && (
          <div style={{ display: "flex" }}>
            <Filter
              filtertype="Brugernavn"
              filterValue={brugernavnFilter}
              removeFilter={() => {
                setBrugernavn("");
                setPageOptions({ ...pageOptions, navn: "" });
              }}
            />
          </div>
        )}
        {pageOptions.rolleid && (
          <div style={{ display: "flex" }}>
            <Filter
              filtertype="Rolle"
              filterValue={rolleFilterValue()}
              removeFilter={() => {
                setRolleId("");
                setPageOptions({ ...pageOptions, rolleid: "" });
              }}
            />
          </div>
        )}
        {kommunenummerFilter.map((knr) => {
          return (
            <div key={knr} style={{ display: "flex" }}>
              <Filter
                filtertype="Organisation"
                filterValue={
                  skatPlusKommuner.find((k) => k.knr === knr)?.navn ??
                  knr.toString()
                }
                removeFilter={() => {
                  setKommuneIsActive(knr, false, true);
                }}
              />
            </div>
          );
        })}
      </div>
      <ul role="tablist">
        <li className={styles.listHeader}>
          <div className={styles.navn}>
            <SortButton
              pageOptions={pageOptions}
              setPageOptions={setPageOptions}
              title="Navn"
              descSortOption={BrugerSortOrder.NavnDesc}
              ascSortOption={BrugerSortOrder.NavnAsc}
            />
          </div>
          <div className={styles.org}>
            <SortButton
              pageOptions={pageOptions}
              setPageOptions={setPageOptions}
              title="Organisation"
              descSortOption={BrugerSortOrder.OrganisationDesc}
              ascSortOption={BrugerSortOrder.OrganisationAsc}
            />
          </div>
          <div className={styles.cvr}>
            <h2 className="h4">CVR</h2>
          </div>
          <div className={styles.cvr}>
            <SortButton
              pageOptions={pageOptions}
              setPageOptions={setPageOptions}
              title="Seneste login"
              descSortOption={BrugerSortOrder.SenesteLoginDesc}
              ascSortOption={BrugerSortOrder.SenesteLoginAsc}
            />
          </div>
          <div className={styles.roller}>
            <h2 className="h4">Antal roller</h2>
          </div>
        </li>
        {brugere.map((bruger, _i) => {
          const senesteLoginDate = new Date(bruger.senestelogin) ?? null;
          return (
            <li key={bruger.id}>
              <motion.button
                role="tab"
                aria-controls="artikel-main-content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={styles.opgave}
                title={bruger.navn}
                onClick={() => {
                  navigate(`/admin?type=brugere&brugerid=${bruger.id}`);
                }}
              >
                <div className={styles.listrow}>
                  <div className={styles.navn}>
                    <h2 className="h4">{bruger.navn}</h2>
                  </div>
                  <div className={styles.org}>{bruger.organisationNavn}</div>
                  <div className={styles.cvr}>{bruger.cvr}</div>
                  <div className={styles.cvr}>
                    {" "}
                    {senesteLoginDate && senesteLoginDate?.getFullYear() > 1
                      ? senesteLoginDate.toLocaleDateString("da-dk")
                      : "-"}
                  </div>
                  <div className={styles.roller}>{bruger.roller.length}</div>
                </div>
              </motion.button>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function SortButton(props: {
  pageOptions: BrugerePageOptions;
  setPageOptions: Dispatch<SetStateAction<BrugerePageOptions>>;
  title: string;
  descSortOption: BrugerSortOrder;
  ascSortOption: BrugerSortOrder;
}) {
  const { pageOptions, setPageOptions, title } = props;
  return (
    <button
      className="h4"
      style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
      onClick={() => {
        if (pageOptions.sortOrder === props.ascSortOption) {
          setPageOptions({
            ...pageOptions,
            sortOrder: props.descSortOption,
          });
        } else {
          setPageOptions({
            ...pageOptions,
            sortOrder: props.ascSortOption,
          });
        }
      }}
    >
      {title}
      <span
        className={`d-flex flex-column${
          pageOptions.sortOrder !== props.ascSortOption &&
          pageOptions.sortOrder !== props.descSortOption
            ? " text-muted"
            : ""
        }`}
      >
        {pageOptions.sortOrder === props.ascSortOption ||
        (pageOptions.sortOrder !== props.ascSortOption &&
          pageOptions.sortOrder !== props.descSortOption) ? (
          <ChevronUp />
        ) : (
          <span>&nbsp;</span>
        )}
        {pageOptions.sortOrder === props.descSortOption ||
        (pageOptions.sortOrder !== props.ascSortOption &&
          pageOptions.sortOrder !== props.descSortOption) ? (
          <ChevronDown />
        ) : (
          <span>&nbsp;</span>
        )}
      </span>
    </button>
  );
}
